// client/src/pages/admin/AdminReports.js
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import StockPerformance from './StockPerformance';
import { AuthContext } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { API_BASE_URL } from '../../utils/media';

const Stat = ({ label, value, note }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{label}</Typography>
      <Typography variant="h4" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
      {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
    </CardContent>
  </Card>
);

// One measure per row, so one color: the bar shows size, and the exact figure sits beside it.
const ValueBar = ({ value, max, label }) => (
  <Tooltip title={label}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 150 }}>
      <Box sx={{ flexGrow: 1, height: 10, bgcolor: 'action.hover' }}>
        <Box sx={{ height: '100%', width: `${max > 0 ? Math.max((value / max) * 100, 2) : 0}%`, bgcolor: 'primary.main' }} />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{label}</Typography>
    </Stack>
  </Tooltip>
);

const Section = ({ title, action, children }) => (
  <Box sx={{ mb: 4 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
      <Typography variant="h6" component="h2">{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>
);

const Empty = ({ children }) => <Typography variant="body2" color="text.secondary">{children}</Typography>;

// A leading = + - or @ would make a spreadsheet treat a product name as a formula.
const csvCell = (value) => {
  const text = String(value ?? '');
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const AdminReports = () => {
  const { token } = useContext(AuthContext);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setError('');
    fetch(`${API_BASE_URL}/api/admin/reports?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Could not load the reports.'))))
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [token, days]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress size={28} />;

  const exportCsv = () => {
    const rows = [
      [`Sales report, last ${data.days} days`],
      [],
      ['Bestsellers'], ['Product', 'Units', 'Revenue'],
      ...data.bestsellers.map((p) => [p.name, p.units, Number(p.revenue).toFixed(2)]),
      [],
      ['Sales by category'], ['Category', 'Units', 'Revenue'],
      ...data.byCategory.map((c) => [c.category, c.units, Number(c.revenue).toFixed(2)]),
    ];
    const url = URL.createObjectURL(new Blob([rows.map((row) => row.map(csvCell).join(',')).join('\n')], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales-report-${data.days}-days.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const maxBestseller = Math.max(...data.bestsellers.map((p) => Number(p.units)), 0);
  const maxCategory = Math.max(...data.byCategory.map((c) => Number(c.revenue)), 0);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
        <Typography variant="h5">Reports</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup exclusive size="small" value={days} onChange={(e, next) => next && setDays(next)} aria-label="Report period">
            <ToggleButton value={30} sx={{ textTransform: 'none' }}>30 days</ToggleButton>
            <ToggleButton value={90} sx={{ textTransform: 'none' }}>90 days</ToggleButton>
            <ToggleButton value={365} sx={{ textTransform: 'none' }}>1 year</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>Export sales</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}><Stat label="Paid sales" value={formatCurrency(data.revenue)} note={`${data.orderCount} paid order${data.orderCount === 1 ? '' : 's'} in this period`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Stat label="Awaiting EFT" value={formatCurrency(data.unpaidTotal)} note={`${data.unpaidOrders.length} unpaid order${data.unpaidOrders.length === 1 ? '' : 's'}, of any age`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Stat label="Stock value" value={formatCurrency(data.stockRetailValue)} note={`${data.stockUnits} units, at selling price`} /></Grid>
        <Grid item xs={12} sm={6} lg={3}><Stat label="Back-in-stock requests" value={data.backInStockRequests} note="Customers waiting for an email" /></Grid>
      </Grid>

      <StockPerformance days={days} token={token} />

      <Section title="Awaiting EFT payment" action={<Button size="small" component={Link} to="/admin/orders">Open orders</Button>}>
        {data.unpaidOrders.length === 0 ? <Empty>No orders are waiting for payment.</Empty> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Order</TableCell><TableCell>Customer</TableCell><TableCell align="right">Total</TableCell><TableCell align="right">Waiting</TableCell></TableRow></TableHead>
              <TableBody>
                {data.unpaidOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell><Link to={`/orders/${order.id}`}>#{order.id}</Link></TableCell>
                    <TableCell>{order.customer_email}</TableCell>
                    <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell align="right">{order.daysWaiting === 0 ? 'Today' : `${order.daysWaiting} day${order.daysWaiting === 1 ? '' : 's'}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section title={`Low stock (${data.lowStockLevel} or fewer)`}>
        {data.lowStock.length === 0 ? <Empty>Everything is well stocked.</Empty> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">In stock</TableCell></TableRow></TableHead>
              <TableBody>
                {data.lowStock.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell><Link to={`/products/${product.id}`}>{product.name}</Link></TableCell>
                    <TableCell align="right">
                      {Number(product.quantity) <= 0
                        ? <Chip size="small" label="Sold out" color="error" />
                        : Number(product.quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section title="Bestsellers">
        {data.bestsellers.length === 0 ? <Empty>No paid sales in this period yet.</Empty> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Product</TableCell><TableCell>Units sold</TableCell><TableCell align="right">Revenue</TableCell></TableRow></TableHead>
              <TableBody>
                {data.bestsellers.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{product.name}</TableCell>
                    <TableCell><ValueBar value={Number(product.units)} max={maxBestseller} label={`${product.units} unit${Number(product.units) === 1 ? "" : "s"}`} /></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(product.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Section title="Sales by category">
        {data.byCategory.length === 0 ? <Empty>No paid sales in this period yet.</Empty> : (
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Category</TableCell><TableCell>Revenue</TableCell><TableCell align="right">Units</TableCell></TableRow></TableHead>
              <TableBody>
                {data.byCategory.map((category) => (
                  <TableRow key={category.category} hover>
                    <TableCell>{category.category}</TableCell>
                    <TableCell><ValueBar value={Number(category.revenue)} max={maxCategory} label={formatCurrency(category.revenue)} /></TableCell>
                    <TableCell align="right">{category.units}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
    </Box>
  );
};

export default AdminReports;
